/**
 * Estado do guia passo a passo. O que precisa sobreviver a recarregar a página (o "Ver leads" da
 * aba da captura recarrega o painel) fica no navegador, por pessoa, por 7 dias sem uso
 * (proposta, seção 5): o passo, o tipo de busca, o que foi preenchido e quando a busca começou.
 * Convite, janela aberta, cartão recolhido e o toast de saída são só desta carga.
 */
import { create } from 'zustand';
import type { OnboardingResult } from '../onboarding/OnboardingDialog';
import { isPasso, type GuiaModulo, type PassoId } from './passos';

export type GuiaOrigem = 'convite' | 'menu' | 'inicio';
export type GuiaStatus = 'desligado' | 'ativo' | 'concluido';
export type GuiaJanela = 'convite' | 'escolher' | null;

/** O que fica guardado no navegador. */
export interface GuiaSalvo {
  status: GuiaStatus;
  passo: PassoId;
  origem: GuiaOrigem;
  modulo: GuiaModulo | null;
  /** Termo que o guia preencheu ("indústria"); vazio quando a pessoa escreveu o dela. */
  termo: string;
  /** Rede preenchida pelo guia nas buscas de telefones e emails ("LinkedIn"). */
  rede: string;
  /** Quando a busca começou (ms), para achar a lista dela depois de recarregar. */
  inicioBusca: number | null;
  listaId: string | null;
  /** Contatos da lista no fim da busca. */
  contatos: number;
  atualizadoEm: number;
}

interface GuiaState extends GuiaSalvo {
  /** Conta carregada (o estado salvo é por pessoa). */
  userId: string;
  janela: GuiaJanela;
  /** Perfil recém-respondido no onboarding (o convite vem dele). */
  perfil: OnboardingResult | null;
  recolhido: boolean;
  /** Balão "O guia fica aqui" depois de "Agora não". */
  balaoConta: boolean;
  /** "Sair do guia": o estado anterior, para o "Desfazer" do toast. */
  saida: GuiaSalvo | null;

  carregar: (userId: string) => void;
  abrirConvite: (perfil: OnboardingResult) => void;
  recusarConvite: () => void;
  fecharBalaoConta: () => void;
  comecar: (origem: GuiaOrigem, perfil?: OnboardingResult | null) => void;
  abrirEscolha: () => void;
  fecharJanela: () => void;
  ir: (passo: PassoId, patch?: Partial<GuiaSalvo>) => void;
  recolher: (recolhido: boolean) => void;
  sair: () => void;
  desfazerSaida: () => void;
  fecharToast: () => void;
  encerrar: () => void;
}

const VALIDADE_MS = 7 * 24 * 60 * 60 * 1000;
/** Voltar ao painel depois disso (outro dia, outra sessão) traz o cartão recolhido. */
const RECOLHER_DEPOIS_MS = 30 * 60 * 1000;
const PREFIXO = 'guia_v1_';
/** Passos que saíram em 28/10/2026 (o guia termina na lista): quem parou neles volta à lista. */
const PASSOS_QUE_SAIRAM: readonly unknown[] = ['abrir_contato', 'usar_contato'];

const VAZIO: GuiaSalvo = {
  status: 'desligado',
  passo: 'escolher',
  origem: 'menu',
  modulo: null,
  termo: '',
  rede: '',
  inicioBusca: null,
  listaId: null,
  contatos: 0,
  atualizadoEm: 0,
};

function chave(userId: string): string {
  return PREFIXO + userId;
}

export function lerSalvo(userId: string, agora = Date.now()): GuiaSalvo | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(chave(userId));
    if (!raw) return null;
    const data = JSON.parse(raw) as Partial<GuiaSalvo>;
    if (PASSOS_QUE_SAIRAM.includes(data.passo)) data.passo = 'abrir_lista';
    if (!isPasso(data.passo) || (data.status !== 'ativo' && data.status !== 'concluido'))
      return null;
    if (typeof data.atualizadoEm !== 'number' || agora - data.atualizadoEm > VALIDADE_MS) {
      localStorage.removeItem(chave(userId));
      return null;
    }
    const modulo =
      data.modulo === 'places' || data.modulo === 'phones' || data.modulo === 'emails'
        ? data.modulo
        : null;
    return {
      status: data.status,
      passo: data.passo,
      origem: data.origem === 'convite' || data.origem === 'inicio' ? data.origem : 'menu',
      modulo,
      termo: typeof data.termo === 'string' ? data.termo.slice(0, 120) : '',
      rede: typeof data.rede === 'string' ? data.rede.slice(0, 60) : '',
      inicioBusca: typeof data.inicioBusca === 'number' ? data.inicioBusca : null,
      listaId: typeof data.listaId === 'string' ? data.listaId.slice(0, 64) : null,
      contatos: typeof data.contatos === 'number' && data.contatos >= 0 ? data.contatos : 0,
      atualizadoEm: data.atualizadoEm,
    };
  } catch {
    return null;
  }
}

function gravar(userId: string, salvo: GuiaSalvo): void {
  if (!userId) return;
  try {
    if (salvo.status === 'desligado') localStorage.removeItem(chave(userId));
    else localStorage.setItem(chave(userId), JSON.stringify(salvo));
  } catch {
    /* armazenamento indisponível: o guia vale só nesta carga */
  }
}

function salvoDe(state: GuiaState): GuiaSalvo {
  return {
    status: state.status,
    passo: state.passo,
    origem: state.origem,
    modulo: state.modulo,
    termo: state.termo,
    rede: state.rede,
    inicioBusca: state.inicioBusca,
    listaId: state.listaId,
    contatos: state.contatos,
    atualizadoEm: state.atualizadoEm,
  };
}

export const useGuiaStore = create<GuiaState>((set, get) => {
  /** Muda o estado salvo e grava. */
  const mudar = (patch: Partial<GuiaSalvo>, extra: Partial<GuiaState> = {}): void => {
    set({ ...patch, ...extra, atualizadoEm: Date.now() });
    const state = get();
    gravar(state.userId, salvoDe(state));
  };

  return {
    ...VAZIO,
    userId: '',
    janela: null,
    perfil: null,
    recolhido: false,
    balaoConta: false,
    saida: null,

    carregar: (userId) => {
      if (get().userId === userId) return;
      const salvo = lerSalvo(userId);
      const longe = salvo ? Date.now() - salvo.atualizadoEm > RECOLHER_DEPOIS_MS : false;
      set({
        ...VAZIO,
        ...(salvo ?? {}),
        userId,
        janela: null,
        perfil: null,
        recolhido: Boolean(salvo && salvo.status === 'ativo' && longe),
        balaoConta: false,
        saida: null,
      });
    },
    abrirConvite: (perfil) => set({ janela: 'convite', perfil }),
    recusarConvite: () => set({ janela: null, balaoConta: true }),
    fecharBalaoConta: () => set({ balaoConta: false }),
    comecar: (origem, perfil) =>
      mudar(
        { ...VAZIO, status: 'ativo', passo: 'escolher', origem },
        {
          janela: 'escolher',
          perfil: perfil ?? get().perfil,
          recolhido: false,
          balaoConta: false,
          saida: null,
        },
      ),
    abrirEscolha: () => set({ janela: 'escolher', recolhido: false }),
    fecharJanela: () => set({ janela: null }),
    ir: (passo, patch = {}) => mudar({ ...patch, passo }),
    recolher: (recolhido) => set({ recolhido }),
    sair: () => {
      const anterior = salvoDe(get());
      mudar({ ...VAZIO }, { janela: null, saida: anterior, recolhido: false });
    },
    desfazerSaida: () => {
      const anterior = get().saida;
      if (anterior) mudar({ ...anterior }, { saida: null });
    },
    fecharToast: () => set({ saida: null }),
    encerrar: () =>
      mudar({ status: 'concluido', passo: 'fim' }, { janela: null, recolhido: false }),
  };
});

/** O guia (ou o convite dele) está na tela: as outras janelas e dicas esperam (proposta, seção 6). */
export function guiaOcupado(state: Pick<GuiaState, 'status' | 'janela'>): boolean {
  return state.status === 'ativo' || state.janela !== null;
}

export function useGuiaOcupado(): boolean {
  return useGuiaStore((s) => guiaOcupado(s));
}
